import { ApiResponseType, makeRequest } from "../helpers/api.helpers";
import { createDebounce } from "../helpers/debounce.helper";
import { IVehicleVisualRecord } from "../../types/vehicle-visual-record.type";
import { IVehicleLinkResponse } from "../../modules/vehicles/components/settings-blocks/VehicleInfoCard";
import { IVehicle, VehicleRedoPreparationType } from "../../types/vehicle.type";
import { getCompanyId } from "./company.api";
import { IWorkOrder } from "../../types/workorder.types";
import { VehiclePlate } from "../../pages/PickupRace/PickupRace";
import { inputPickupRaceForm } from "../../pages/PickupRace/PickupRaceForm";

const API_URL = import.meta.env.VITE_API_URL;
const makeRequestDebounced = createDebounce(makeRequest, 250);

type GetVehiclesPagedParams = {
  page?: number;
  itemsPerPage?: number;
  search?: string;
  fields?: string;
  partnerId?: string;
  companyId?: string;
  status?: string | string[];
  listAll?: boolean;
  renterId?: string;
  exchangeForRenterId?: string;
  licensePlates?: string;
  [key: string]: string | number | boolean | string[] | undefined;
};

export async function getVehicles({
  status = [] as string[],
  search = "",
  startDate = "",
  endDate = "",
  startDateAtualizadoEm = "",
  endDateAtualizadoEm = "",
  page = 1,
  itemsPerPage = "",
  orderBy = "updatedAt",
  partnerId = "",
  fields = "_id,brand,model,licensePlate,status,updatedAt,createdAt,vehiclePreparing,partnerId, partnerInfo.companyName,partnerInfo.partnerId, fineUpload, odometerLocavibe, odometer, vehicleSituation, insuranceStatus, pathReimbursement, companyId, availableForRent",
  insuranceStatusOptionsData = "",
} = {}) {
  const params = new URLSearchParams({
    page: String(page),
    itemsPerPage: String(itemsPerPage),
    fields,
  });

  if (insuranceStatusOptionsData["value"] != "")
    insuranceStatusOptionsData &&
      params.append(
        "filters[insuranceStatus]",
        insuranceStatusOptionsData["value"],
      );
  let statusParam = "";
  if (Array.isArray(status) && status.length > 0) {
    statusParam = status.join(",");
  } else if (typeof status === "string") {
    statusParam = status;
  }
  status && params.append("filters[status]", statusParam);
  search && params.append("search", search);
  orderBy && params.append("orderBy", orderBy);
  startDate && params.append("filters[startDate]", startDate.toString());
  endDate && params.append("filters[endDate]", endDate.toString());
  startDateAtualizadoEm &&
    params.append(
      "filters[startDateAtualizadoEm]",
      startDateAtualizadoEm.toString(),
    );
  endDateAtualizadoEm &&
    params.append(
      "filters[endDateAtualizadoEm]",
      endDateAtualizadoEm.toString(),
    );

  partnerId && params.append("partnerId", partnerId);
  const url = `${API_URL}/vehicles?${params.toString()}`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<
    IVehicle[]
  > & { totalItems: number };

  return { ok: result.ok, data: result.data, totalItems: result.totalItems };
}
export interface PreparationHistoryResponse {
  _id: string;
  licensePlate: string;
  type: string;
  preparator: string;
  items: Record<string, { beforeImage: string; afterImage: string }>;
}

export async function getVehiclePreparationHistory(
  licensePlate: string,
  type?: "preparation" | "repreparation",
  workOrderId?: string,
) {
  const params = new URLSearchParams();
  if (type) {
    params.append("type", type);
  }
  if (workOrderId) {
    params.append("workOrderId", workOrderId);
  }
  const url = `${API_URL}/vehicles/${licensePlate}/preparation-history?${params.toString()}`;

  return (await makeRequest(
    url,
  )) as ApiResponseType<PreparationHistoryResponse>;
}

export async function getLicensingReport({
  status = [] as string[],
  search = "",
  page = 1,
  itemsPerPage = 10,
  orderBy = "updatedAt",
} = {}) {
  const params = new URLSearchParams({
    page: String(page),
    itemsPerPage: String(itemsPerPage),
    orderBy,
  });

  let statusParam = "";
  if (Array.isArray(status) && status.length > 0) {
    statusParam = status.join(",");
  }
  status && params.append("licensingStatus", statusParam);
  search && params.append("search", search);

  const url = `${API_URL}/vehicles/licensing-report?${params.toString()}`;

  const result = (await makeRequestDebounced(url)) as ApiResponseType<
    (IVehicle & { lastLicensing: string; missingLicensing: string })[]
  > & { totalItems: number };

  return { ok: result.ok, data: result.data, totalItems: result.totalItems };
}

export type PartnerByLicensePlate = {
  vehicleId: string;
  licensePlate: string;
  partnerCompanyName: string;
};

export async function getPartnersByLicensePlate(licensePlate: string[]) {
  const params = new URLSearchParams();
  licensePlate.forEach((plate) => params.append("licensePlate[]", plate));

  const url = `${API_URL}/vehicle/partners-by-license-plate?${params.toString()}`;

  try {
    const result = (await makeRequestDebounced(url)) as ApiResponseType<
      PartnerByLicensePlate[]
    >;
    return result;
  } catch (error) {
    console.error("Erro em getPartnersByLicensePlate:", error);
    throw error;
  }
}

export async function makeVehicleAvailableForRent(id: string) {
  const url = `${API_URL}/vehicles/${id}`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ availableForRent: true }),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IVehicle>
  >;

  return result;
}

export async function getVehiclesCompany() {
  const url = `${API_URL}/vehicles/aggregate-by-company`;
  return (await makeRequestDebounced(url)) as ApiResponseType<unknown>;
}

export async function vehiclesDashboardFleetPanel(partnerId?: string) {
  const params = new URLSearchParams({
    partnerId: partnerId || "",
  });
  const url = `${API_URL}/vehicles/dashboard/fleet-panel?${params.toString()}`;
  return (await makeRequest(url)) as ApiResponseType<unknown>;
}

export async function vehiclesDashboardFleetPanelIRF(partnerId?: string) {
  const params = new URLSearchParams();
  if (partnerId) {
    params.append("partnerId", partnerId);
  }

  const url = `${API_URL}/vehicles/dashboard/fleet-panel/irf?${params.toString()}`;
  return (await makeRequest(url)) as ApiResponseType<unknown>;
}

export async function vehiclesDashboardFleetPanelStatuses(partnerId?: string) {
  const params = new URLSearchParams();
  if (partnerId) {
    params.append("partnerId", partnerId);
  }

  const url = `${API_URL}/vehicles/dashboard/fleet-panel/statuses?${params.toString()}`;
  return (await makeRequest(url)) as ApiResponseType<unknown>;
}

export type ScopeOperationEntry = {
  companyId: string;
  date: string;
  noteOperation: number;
};

export async function getScoreOperation(params?: {
  startDate?: string;
  endDate?: string;
}, options?: {
  skipScope?: boolean;
}) {
  const searchParams = new URLSearchParams();

  if (params?.startDate) {
    searchParams.append("startDate", params.startDate);
  }
  if (params?.endDate) {
    searchParams.append("endDate", params.endDate);
  }

  const queryString = searchParams.toString();
  const url = `${API_URL}/vehicles/dashboard/scope-operation${
    queryString ? `?${queryString}` : ""
  }`;

  return (await makeRequestDebounced(url, {
    skipScopeHeaders: options?.skipScope,
    skipScopeParams: options?.skipScope,
  })) as ApiResponseType<ScopeOperationEntry[]>;
}

export async function getVehiclesCount(partnerId: string) {
  const params = new URLSearchParams({
    partnerId: partnerId,
  });
  const url = `${API_URL}/vehicles/count?${params.toString()}`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<IVehicle>;

  return { ok: result.ok, data: result.data };
}

export async function getVehiclesByPartnerId(
  data: GetVehiclesPagedParams,
): Promise<ApiResponseType<IVehicle[]>> {
  const params = new URLSearchParams();
  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      value.forEach((v) => params.append(key, String(v)));
      return;
    }

    params.append(key, String(value));
  });

  const url = `${API_URL}/vehicles/partner?${params.toString()}`;

  const result = (await makeRequestDebounced(url)) as Promise<
    ApiResponseType<IVehicle[]>
  >;
  return result;
}

export async function getVehiclesPaged(
  data: GetVehiclesPagedParams,
): Promise<ApiResponseType<IVehicle[]>> {
  const params = new URLSearchParams();

  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      value.forEach((v) => params.append(key, String(v)));
      return;
    }

    params.append(key, String(value));
  });

  const url = `${API_URL}/vehicles?${params.toString()}`;

  return (await makeRequestDebounced(url)) as ApiResponseType<IVehicle[]>;
}

export async function createVehicle(vehicleData: Partial<IVehicle>) {
  const data = {
    ...vehicleData,
    companyId: getCompanyId(),
  };
  const url = `${API_URL}/vehicles`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };

  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IVehicle>
  >;
}

const preparationVehicleDebounced = createDebounce(makeRequest, 200);
export async function preparationVehicle(
  licensePlate: string,
  model: string,
  data?: VehicleRedoPreparationType,
) {
  const url = `${API_URL}/vehicle/preparation`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      licensePlate,
      companyId: getCompanyId(),
      model,
      ...data,
    }),
  };
  return preparationVehicleDebounced(url, options) as Promise<
    ApiResponseType<IWorkOrder>
  >;
}

const changeSituationDebounced = createDebounce(makeRequest, 200);

export async function changeSituation(
  vehicleId: string,
  vehicleSituation: string,
) {
  const url = `${API_URL}/vehicles/changestatus`;
  const options = {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      vehicleId,
      vehicleSituation,
    }),
  };
  return changeSituationDebounced(url, options) as Promise<
    ApiResponseType<IWorkOrder>
  >;
}

export async function updateVehicle(
  vehicleId: string,
  vehicleData: Partial<IVehicle> & { odometer?: string | number },
) {
  delete vehicleData.status;
  delete vehicleData.updatedAt;
  delete vehicleData.createdAt;

  if (vehicleData.odometer !== undefined && vehicleData.odometer !== null) {
    vehicleData.odometer = String(vehicleData.odometer);
  }

  const url = `${API_URL}/vehicles/${vehicleId}`;
  const options = {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(vehicleData),
  };
  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IVehicle>
  >;
}

const makeRequestDebouncedForGetVehicleById = createDebounce(makeRequest, 250);
export async function getVehicleById(vehicleId: string) {
  const url = `${API_URL}/vehicles/${vehicleId}`;
  const result = (await makeRequestDebouncedForGetVehicleById(
    url,
  )) as ApiResponseType<IVehicle>;

  return result;
}

export async function verifyTrackerVehicle(vehicleId: string) {
  const url = `${API_URL}/vehicles/${vehicleId}/verify-tracker`;
  const result = (await makeRequestDebounced(
    url,
  )) as ApiResponseType<IVehicleVisualRecord>;

  return result;
}

export async function syncVelotrackData(licensePlate: string) {
  const url = `${API_URL}/vehicles/${licensePlate}/velotrack-sync`;
  const options = {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ licensePlate: licensePlate }),
  };
  const result = makeRequestDebounced(
    url,
    options,
  ) as unknown as ApiResponseType<IVehicle>;
  return result;
}

type SaveVehicleRecordsProps = {
  renterId: string | undefined;
  vehicleId: string;
  photos?: { content: string }[];
  photosMaintenance?: { content: string }[];
  nameRenter?: string;
  licensePlate?: string;
  returnReason?: string;
  withoutPhotos?: boolean;
  complement?: string;
};

export async function savePublickLink(data: IVehicle | string) {
  const url = `${API_URL}/vehicles/public-link`;

  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return makeRequest<IVehicleLinkResponse>(url, options);
}

export type searchData = {
  vehicleId: string;
  renterId: string;
};

export async function getVisualRecords(searchData: searchData) {
  const queryString = new URLSearchParams(
    searchData as Record<string, string>,
  ).toString();
  const url = `${API_URL}/visual-records?${queryString}`;
  const result = (await makeRequestDebounced(
    url,
  )) as ApiResponseType<IVehicleVisualRecord>;

  return result;
}

export async function getVisualRecordsById(id: string) {
  const url = `${API_URL}/visual-records/${id}`;
  const result = (await makeRequestDebounced(
    url,
  )) as ApiResponseType<IVehicleVisualRecord>;

  return result;
}

export async function getImageVehicle(searchId: string) {
  const url = `${API_URL}/vehicles/visual-records/${searchId}`;
  const result = (await makeRequestDebounced(
    url,
  )) as ApiResponseType<IVehicleVisualRecord>;

  return result;
}

export async function saveVehicleRecords(data: SaveVehicleRecordsProps) {
  const url = `${API_URL}/vehicles/visual-records`;

  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return makeRequest(url, options);
}

export async function updateVehicleVisualRecords(
  data: SaveVehicleRecordsProps,
) {
  const url = `${API_URL}/vehicles/visual-records/update`;
  const options = {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<SaveVehicleRecordsProps>
  >;
}

const makeRequestDebouncedMaintenance = createDebounce(makeRequest, 250);
export async function getImageVehicleMaintenance(searchId: string) {
  const url = `${API_URL}/vehicles/maintenance/visual-records/${searchId}`;
  const result = (await makeRequestDebouncedMaintenance(
    url,
  )) as ApiResponseType<IVehicleVisualRecord>;

  return result;
}

export async function updateVehicleProps(
  id: string,
  updatedData: Partial<IVehicle>,
) {
  const url = `${API_URL}/vehicles/${id}/documents`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updatedData),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IVehicle>
  >;

  return result;
}

export async function updateVehicleDocument(
  id: string,
  updatedData: Partial<IVehicle>,
) {
  const url = `${API_URL}/vehicles/${id}/payments-uploads`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updatedData),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IVehicle>
  >;

  return result;
}

export async function updateVehicleOneProps(
  id: string,
  updatedData: Partial<IVehicle>,
) {
  const url = `${API_URL}/vehicles/${id}`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updatedData),
  };
  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IVehicle>
  >;
  return result;
}

export async function toggleVehicleBlockStatus(
  id: string,
  updatedData: Partial<IVehicle>,
) {
  const url = `${API_URL}/vehicles/${id}/toggle-block`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updatedData),
  };
  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IVehicle>
  >;

  return result;
}

type FinePaymentProps = {
  vehicleId?: string;
  renterCpf?: string;
  paymentMethod?: string;
  reference?: string;
  otherReason?: string;
  reserveDepositInstallments?: string;
  depositInstallments?: string;
  installmentOption?: string;
  deposit?: number;
  subscriptionInitialDate?: string;
  fines?: Fine[];
  isChecked?: boolean;
};

type Fine = {
  value: string;
  description: string;
};

export async function createAsaasFine(data: FinePaymentProps) {
  const url = `${API_URL}/integration/fine`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<unknown>
  >;

  return result;
}

export async function getPickupRace() {
  const url = `${API_URL}/vehicles/pickup-race`;

  const result = (await makeRequestDebounced(url)) as Promise<
    ApiResponseType<VehiclePlate[]>
  >;

  return result;
}

export const postVehiclespickupRacelinkCollector = async (
  data: inputPickupRaceForm,
  companyId: string,
) => {
  const url = `${API_URL}/vehicles/pickup-race/repossession/link-collector/${companyId}`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return (await makeRequestDebounced(url, options)) as Promise<
    ApiResponseType<unknown>
  >;
};

export async function retrieveVehicleLicensePlate(
  licensePlate: string | undefined,
): Promise<ApiResponseType<IVehicle>> {
  const url = `${API_URL}/vehicles/${licensePlate}/licensePlate`;

  return (await makeRequestDebounced(url)) as ApiResponseType<IVehicle>;
}

export const convertedImageToTextPost = async (
  data: string,
): Promise<ApiResponseType<string>> => {
  const url = `${API_URL}/converted/licensePlateToText`;

  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ imageBase64: data }),
  };

  return (await makeRequestDebounced(url, options)) as ApiResponseType<string>;
};

export const odometerToTextPost = async (
  data: string,
): Promise<ApiResponseType<unknown>> => {
  const url = `${API_URL}/converted/odometerToText`;

  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ imageBase64: data }),
  };

  return (await makeRequestDebounced(url, options)) as ApiResponseType<unknown>;
};

export const maintenanceUpdateOnlyOdometer = async (
  odometerNumber: string,
  maintenanceId: string,
): Promise<ApiResponseType<unknown>> => {
  const url = `${API_URL}/maintenance/update-only-odometer`;

  const options = {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ odometerNumber, maintenanceId }),
  };

  return (await makeRequestDebounced(url, options)) as ApiResponseType<unknown>;
};

export async function getVehicleTrackers({
  status = [],
  search = "",
  startDate = "",
  endDate = "",
  startDateAtualizadoEm = "",
  endDateAtualizadoEm = "",
  page = 1,
  itemsPerPage = 10,
  orderBy = "velotrackSettings.lastComunication",
  fields = "_id, companyId, licensePlate, velotrackSettings, partnerInfo, status, model",
} = {}) {
  const params = new URLSearchParams({
    page: String(page),
    itemsPerPage: String(itemsPerPage),
    fields,
  });
  let statusParam = "";
  if (Array.isArray(status) && status.length > 0) {
    statusParam = status.join(",");
  } else if (typeof status === "string") {
    statusParam = status;
  }
  status && params.append("filters[status]", statusParam);
  search && params.append("search", search);
  orderBy && params.append("orderBy", orderBy);
  startDate && params.append("filters[startDate]", startDate.toString());
  endDate && params.append("filters[endDate]", endDate.toString());
  startDateAtualizadoEm &&
    params.append(
      "filters[startDateAtualizadoEm]",
      startDateAtualizadoEm.toString(),
    );
  endDateAtualizadoEm &&
    params.append(
      "filters[endDateAtualizadoEm]",
      endDateAtualizadoEm.toString(),
    );

  const url = `${API_URL}/vehicles/trackers?${params.toString()}`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<
    IVehicle[]
  > & { totalItems: number; pageTotalItens: number };
  return {
    ok: result.ok,
    data: result.data,
    totalItems: result.totalItems,
    pageTotalItens: result.pageTotalItens,
  };
}

interface AddPhotoVisualRecordData {
  fileName: string;
  visualRecordId: string;
  photos: {
    content: string;
    type: string;
  }[];
}

export async function addPhotoVisualRecord(
  data: AddPhotoVisualRecordData,
): Promise<ApiResponseType<IVehicleVisualRecord>> {
  const url = `${API_URL}/vehicles/visual-records/addFile`;

  const options = {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };

  const result = (await makeRequestDebounced(
    url,
    options,
  )) as ApiResponseType<IVehicleVisualRecord>;
  return result;
}

export async function updateVehicleVisualRecordField(
  visualRecordId: string,
  key: "switchReason" | "returnReason",
  newValue: string,
): Promise<ApiResponseType<IVehicleVisualRecord>> {
  const url = `${API_URL}/vehicles/visual-records/${visualRecordId}`;

  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ [key]: newValue }),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IVehicleVisualRecord>
  >;
  return result;
}
